/*
 * Purpose: Contextual configuration — Swiss-army blocks defined only when placed on a Mission
 * Last modified: 2026-06-27 | Agent
 * Completeness: 100
 */

/** How the block participates in the Mission graph */
export type BlockIoDirection = 'call' | 'receive' | 'send';

/** Semantic role — planner and executor hints; subtype refines behavior */
export type BlockSemanticRole =
  | 'anchor'
  | 'transform'
  | 'action'
  | 'call'
  | 'receive'
  | 'send'
  | 'skill'
  | 'auth'
  | 'vault_chip'
  | 'agent'
  | 'drone'
  | 'tool';

export type PlacementStatus = 'unconfigured' | 'connected' | 'ready';

/**
 * Contextual configuration — stored on `node.config`.
 * A block is a Swiss-army primitive until first placed; placement + context define
 * how it connects and what it does in this Mission (including acting as an agent or drone).
 */
export interface ContextualConfiguration {
  contextLabel?: string;
  contextPurpose?: string;
  ioDirection?: BlockIoDirection;
  semanticRole?: BlockSemanticRole;
  /** Set when the block is first dropped on a Mission map */
  placedAt?: string;
  missionId?: string;
  placementStatus?: PlacementStatus;
  /** Suggested upstream/downstream connection hints (AI or user) */
  connectsFrom?: string;
  connectsTo?: string;
}

/** @deprecated Use ContextualConfiguration */
export type BlockContextFields = ContextualConfiguration;

export const SWISS_ARMY_BLOCK_HELP =
  'Every block is a Swiss-army primitive — versatile until placed. Contextual configuration is what tells it how to connect and what to do here.';

export const CONTEXTUAL_CONFIGURATION_HELP =
  'Name this block and describe its job in this Mission. The engine type is the tool; context is the role — including agent or drone.';

export function withContextualConfiguration(
  config: Record<string, unknown>,
  fields: ContextualConfiguration
): Record<string, unknown> {
  return { ...config, ...fields };
}

export function getContextualDisplayName(
  config: Record<string, unknown>,
  fallback: string
): string {
  const label = String(config.contextLabel ?? config.name ?? '').trim();
  return label || fallback;
}

export function inferSemanticRole(type: string, subtype: string): BlockSemanticRole {
  if (type === 'start' || type === 'goal') return 'anchor';
  if (type === 'agent') return 'agent';
  if (type === 'drone') return 'drone';
  if (type === 'tube') return 'tool';
  if (type === 'branded') return 'tool';
  if (type === 'io') {
    if (subtype === 'call') return 'call';
    if (subtype === 'receive') return 'receive';
    if (subtype === 'send') return 'send';
    return 'tool';
  }
  if (type === 'skill') return 'skill';
  if (type === 'auth') return 'auth';
  if (type === 'vault') return 'vault_chip';
  if (type === 'tool' && subtype === 'transform') return 'transform';
  if (type === 'action') return 'action';
  if (type === 'trigger') return 'receive';
  if (type === 'tool' && subtype === 'http') return 'call';
  if (type === 'action' && (subtype === 'webhook' || subtype === 'notification')) return 'send';
  return 'tool';
}

export function initializePlacementContext(
  missionId: string,
  type: string,
  subtype: string,
  blockTypeLabel: string
): Record<string, unknown> {
  const semanticRole = inferSemanticRole(type, subtype);
  const base: ContextualConfiguration = {
    contextLabel: blockTypeLabel,
    contextPurpose: '',
    semanticRole,
    placedAt: new Date().toISOString(),
    missionId,
    placementStatus: 'unconfigured',
  };

  if (semanticRole === 'call' || subtype === 'http') {
    base.ioDirection = 'call';
  } else if (semanticRole === 'receive' || type === 'trigger') {
    base.ioDirection = 'receive';
  } else if (semanticRole === 'send') {
    base.ioDirection = 'send';
  } else if (semanticRole === 'agent') {
    base.ioDirection = 'call';
  } else if (semanticRole === 'drone') {
    base.ioDirection = 'send';
  }

  return { ...base };
}

/** @deprecated Use initializePlacementContext */
export function defaultContextualConfiguration(
  type: string,
  subtype: string,
  blockTypeLabel: string
): Record<string, unknown> {
  return initializePlacementContext('', type, subtype, blockTypeLabel);
}

export const IO_DIRECTION_HINTS: Record<BlockIoDirection, string> = {
  call: 'Call anything — API, agent hand-off, or service (context defines the target)',
  receive: 'Receive anything — webhook, inbox, agent result, or event',
  send: 'Send anything — drone dispatch, POST, notification, or hand-off',
};

export const AGENT_BLOCK_HELP =
  'An Agent block reasons and decides in this Mission — autonomy and brief are set by contextual configuration.';

export const DRONE_BLOCK_HELP =
  'A Drone block executes remotely in this Mission — task and target are set by contextual configuration.';
